import { useEffect } from "react";
import {
  useDataTableSelection,
  type DataTableSelection,
} from "../../../../components/primitives/data-display/DataTable";
import { useSelection, type Selection } from "../../../../hooks/useSelection";
import { getBatchJobItemRowId, type BatchJobItemRow } from "./batch_job_item_rows";

export interface UseBatchJobItemSelectionOptions<Result> {
  rows: BatchJobItemRow<Result>[];
  /** Whether items can be selected at all — only where they await a decision. */
  enabled: boolean;
  /** The page being shown. */
  page: number;
  /** Changes whenever the list is narrowed differently (another tab, another filter). */
  resetKey: string;
}

/** What a list shows of its selection. */
export interface BatchJobItemSelectionView<Result> {
  /** What the data table takes; undefined where selection is not enabled. */
  table: DataTableSelection<BatchJobItemRow<Result>> | undefined;
  count: number;
}

export interface UseBatchJobItemSelectionResult<Result> extends BatchJobItemSelectionView<Result> {
  /** The selected ids themselves, for the actions that settle them. */
  state: Selection;
}

/** The selected items of a list. Selection never survives a change of tab, filter or page — the
 *  selected ids belong to whatever was showing when they were checked. */
export function useBatchJobItemSelection<Result>({
  rows,
  enabled,
  page,
  resetKey,
}: UseBatchJobItemSelectionOptions<Result>): UseBatchJobItemSelectionResult<Result> {
  const state = useSelection();
  const { unselectAll } = state;

  useEffect(() => {
    unselectAll();
  }, [resetKey, page, unselectAll]);

  const table = useDataTableSelection(state, { rows, getRowId: getBatchJobItemRowId, enabled });

  return { table, count: state.count, state };
}
